function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, Fragment } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../common/helpers.js";
const rootClasses = cva([], {
    variants: {
        fullWidth: {
            true: [],
            false: [
                'container'
            ],
            sm: [
                'sm:container'
            ],
            md: [
                'md:container'
            ],
            lg: [
                'lg:container'
            ],
            xl: [
                'xl:container'
            ]
        },
        paddingTop: {
            'none': [],
            'sm': [
                'pt-5'
            ],
            'md': [
                'pt-5 lg:pt-10'
            ],
            'lg': [
                'pt-8 lg:pt-12'
            ],
            'xl': [
                'pt-12 lg:pt-15'
            ],
            '2xl': [
                'pt-15 lg:pt-20'
            ]
        },
        paddingBottom: {
            'none': [],
            'sm': [
                'pb-5'
            ],
            'md': [
                'pb-5 lg:pb-10'
            ],
            'lg': [
                'pb-8 lg:pb-12'
            ],
            'xl': [
                'pb-12 lg:pb-15'
            ],
            '2xl': [
                'pb-15 lg:pb-20'
            ]
        },
        grid: {
            true: [
                'grid',
                'grid-cols-12'
            ],
            false: []
        }
    }
});
const gridColumnClasses = cva([], {
    variants: {
        containerSize: {
            12: [
                'col-span-12'
            ],
            10: [
                'col-span-12',
                'lg:col-span-10'
            ],
            8: [
                'col-span-12',
                'lg:col-span-10',
                'xl:col-span-8'
            ]
        },
        containerAlignment: {
            left: [
                'col-start-1'
            ],
            center: [
                'col-start-1'
            ]
        },
        itemsPerRow: {
            2: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2'
            ],
            3: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2',
                'lg:[&>*]:w-grid-3'
            ],
            4: [
                '[&>*]:w-grid-1',
                'sm:[&>*]:w-grid-2',
                'lg:[&>*]:w-grid-3',
                'xl:[&>*]:w-grid-4'
            ]
        }
    },
    compoundVariants: [
        {
            containerSize: 10,
            containerAlignment: 'center',
            className: [
                'lg:col-start-2'
            ]
        },
        {
            containerSize: 8,
            containerAlignment: 'center',
            className: [
                'lg:col-start-2',
                'xl:col-start-3'
            ]
        },
        {
            itemsPerRow: [
                2,
                3,
                4
            ],
            className: [
                'flex',
                'flex-row',
                'flex-wrap',
                'gap-5'
            ]
        }
    ]
});
/**
 * Container - [API](https://scl.sharp.com/?path=/docs/base-container)
 */ const Container = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { as: Component = 'div', fullWidth = false, paddingTop = 'none', paddingBottom = 'none', containerSize = 12, containerAlignment = 'left', itemsPerRow, className, children } = _param, props = _object_without_properties(_param, [
        "as",
        "fullWidth",
        "paddingTop",
        "paddingBottom",
        "containerSize",
        "containerAlignment",
        "itemsPerRow",
        "className",
        "children"
    ]);
    const hasGridColumn = containerSize !== 12 || !!itemsPerRow;
    const GridColumn = hasGridColumn ? 'div' : Fragment;
    const gridColumnProps = hasGridColumn ? {
        className: gridColumnClasses({
            containerSize,
            containerAlignment,
            itemsPerRow
        })
    } : {};
    return /*#__PURE__*/ _jsx(Component, _object_spread_props(_object_spread({}, props), {
        ref: ref,
        className: cn(rootClasses({
            fullWidth,
            paddingTop,
            paddingBottom,
            grid: hasGridColumn,
            className
        })),
        children: /*#__PURE__*/ _jsx(GridColumn, _object_spread_props(_object_spread({}, gridColumnProps), {
            children: children
        }))
    }));
});
export { Container };
